import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const inputCls = "w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/20";
const labelCls = "block text-xs font-semibold text-muted-foreground mb-1";
const EMPTY = { name: "", facility: "", email: "", phone: "", role: "", message: "" };

export function DemoRequestDialog({ open, onClose }) {
    const [f, setF] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        if (!f.name.trim() || !f.facility.trim() || !f.email.trim()) {
            toast.error("Please provide your name, facility, and email.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/demo-request", {
                name: f.name.trim(), facility: f.facility.trim(), email: f.email.trim(),
                phone: f.phone.trim() || null, role: f.role.trim() || null, message: f.message.trim() || null,
            });
            setDone(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send your request. Please try again.");
        } finally { setSaving(false); }
    };

    const close = () => { setF(EMPTY); setDone(false); onClose(); };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-lg" data-testid="demo-request-dialog">
                {done ? (
                    <div className="py-6 text-center space-y-3">
                        <CheckCircle2 className="w-12 h-12 text-success mx-auto" />
                        <h3 className="font-display font-bold text-xl text-phoenix-navy">Request received!</h3>
                        <p className="text-sm text-muted-foreground">Thanks — our team will reach out to schedule your Blue Phoenix demo shortly.</p>
                        <button data-testid="demo-done" onClick={close} className="mt-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-phoenix-navy text-white">Close</button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-phoenix-orange" /> Request a demo</DialogTitle>
                            <p className="text-sm text-muted-foreground">See how Blue Phoenix supports your facility — tell us a bit and we'll be in touch.</p>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2"><label className={labelCls}>Your name *</label><input data-testid="demo-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                            <div className="col-span-2"><label className={labelCls}>Facility / organization *</label><input data-testid="demo-facility" className={inputCls} value={f.facility} onChange={(e) => set("facility", e.target.value)} placeholder="e.g. County Detention Center" /></div>
                            <div><label className={labelCls}>Work email *</label><input data-testid="demo-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                            <div><label className={labelCls}>Phone</label><input data-testid="demo-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                            <div className="col-span-2"><label className={labelCls}>Your role</label><input data-testid="demo-role" className={inputCls} value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Health Services Administrator, Warden, DON" /></div>
                            <div className="col-span-2"><label className={labelCls}>Anything specific you'd like to see?</label><textarea data-testid="demo-message" rows={3} className={`${inputCls} resize-none`} value={f.message} onChange={(e) => set("message", e.target.value)} /></div>
                        </div>
                        <DialogFooter>
                            <button onClick={close} className="px-4 py-2 rounded-lg text-sm border border-border">Cancel</button>
                            <button data-testid="demo-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-phoenix-navy text-white disabled:opacity-50">
                                {saving ? "Sending…" : "Request demo"}
                            </button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
